import { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Lock, Loader2, RefreshCw, LogOut, Inbox, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "cca_leads_code";

const field =
  "rounded-none border-line bg-white focus-visible:ring-1 focus-visible:ring-navy focus-visible:border-navy";

const btnPrimary =
  "inline-flex items-center justify-center gap-2 border border-ink bg-ink px-7 py-3.5 font-mono text-[12px] uppercase tracking-[0.12em] text-paper transition-colors hover:bg-transparent hover:text-ink disabled:opacity-60";

const btnGhost =
  "inline-flex items-center gap-2 border border-ink px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.12em] text-ink transition-colors hover:bg-ink hover:text-paper disabled:opacity-60";

function fmtDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso || "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function Detail({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-subtext">{label}</p>
      <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-ink">{value}</p>
    </div>
  );
}

function LeadShell({ name, email, phone, linkedin, date, testid, flagged, appCode, onDelete, children }) {
  return (
    <article data-testid={testid} className="border border-line bg-white p-6 md:p-8">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line pb-4">
        <div>
          <h3 className="font-display text-xl font-bold text-ink">{name}</h3>
          <a href={`mailto:${email}`} className="text-sm text-navy hover:underline">
            {email}
          </a>
          {phone && (
            <a href={`tel:${phone}`} className="ml-3 text-sm text-navy hover:underline">
              {phone}
            </a>
          )}
          {linkedin && (
            <a
              href={/^https?:\/\//i.test(linkedin) ? linkedin : `https://${linkedin}`}
              target="_blank"
              rel="noreferrer"
              className="ml-3 text-sm text-navy hover:underline"
            >
              LinkedIn
            </a>
          )}
        </div>
        <div className="flex items-center gap-3">
          {appCode && (
            <span
              data-testid={`${testid}-code-badge`}
              className="border border-navy px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-navy"
            >
              {appCode}
            </span>
          )}
          {flagged && (
            <span
              data-testid={`${testid}-spam-badge`}
              className="border border-clay px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-clay"
            >
              Possible spam
            </span>
          )}
          <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-subtext">{fmtDate(date)}</p>
          {onDelete && (
            <button
              type="button"
              data-testid={`${testid}-delete`}
              onClick={onDelete}
              aria-label="Delete lead"
              className="flex h-8 w-8 items-center justify-center border border-line text-subtext transition-colors hover:border-clay hover:text-clay"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {children}
    </article>
  );
}

export default function Leads() {
  const [code, setCode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState({ contacts: [], opportunities: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (accessCode) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/leads`, {
        headers: { "X-Leads-Code": accessCode },
      });
      if (res.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
        setLeads({ contacts: [], opportunities: [] });
        toast.error("Incorrect access code.");
        return;
      }
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      setLeads({
        contacts: data.contacts || [],
        opportunities: data.opportunities || [],
        stats: data.stats || {},
        site: data.site || null,
      });
      setAuthed(true);
      sessionStorage.setItem(STORAGE_KEY, accessCode);
    } catch {
      toast.error("Could not load leads. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) load(saved);
  }, [load]);

  const unlock = (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error("Please enter the access code.");
      return;
    }
    load(code.trim());
  };

  const signOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setCode("");
    setLeads({ contacts: [], opportunities: [] });
  };

  const remove = async (kind, id) => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/leads/${kind}/${id}`, {
        method: "DELETE",
        headers: { "X-Leads-Code": sessionStorage.getItem(STORAGE_KEY) || "" },
      });
      if (res.status === 401) {
        toast.error("Session expired. Please sign in again.");
        signOut();
        return;
      }
      if (!res.ok) throw new Error("Delete failed");
      setLeads((prev) => ({ ...prev, [kind]: prev[kind].filter((l) => l.id !== id) }));
      toast.success("Lead deleted.");
    } catch {
      toast.error("Could not delete. Please try again.");
    }
  };

  const total = leads.contacts.length + leads.opportunities.length;

  const codeCounts = leads.opportunities.reduce((acc, l) => {
    if (l.application_code) acc[l.application_code] = (acc[l.application_code] || 0) + 1;
    return acc;
  }, {});
  const allCodes = Array.from(
    new Set([...Object.keys(leads.stats || {}), ...Object.keys(codeCounts)])
  ).sort();

  return (
    <div data-testid="leads-page" className="bg-paper pt-16">
      <Helmet>
        <title>Team Leads — CCA</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {!authed ? (
        <section
          data-testid="leads-gate"
          className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-6 py-24"
        >
          <div className="flex h-12 w-12 items-center justify-center bg-ink text-paper">
            <Lock className="h-5 w-5" />
          </div>
          <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-ink">
            Team Leads
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-subtext">
            Internal access only. Enter the access code to view inquiries and opportunity
            applications.
          </p>
          <form onSubmit={unlock} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="leads-code">Access code</Label>
              <Input
                id="leads-code"
                type="password"
                data-testid="leads-code-input"
                className={field}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter access code"
                autoComplete="off"
              />
            </div>
            <button
              type="submit"
              data-testid="leads-unlock-button"
              disabled={loading}
              className={`${btnPrimary} w-full`}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              {loading ? "Checking" : "View Leads"}
            </button>
          </form>
        </section>
      ) : (
        <section
          data-testid="leads-dashboard"
          className="mx-auto max-w-[1400px] px-6 py-16 md:px-12 lg:px-16"
        >
          <div className="flex flex-wrap items-end justify-between gap-6 border-b border-line pb-8">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-subtext">
                Internal
              </p>
              <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-ink md:text-4xl">
                Leads
              </h1>
              <p data-testid="leads-count-total" className="mt-2 text-sm text-subtext">
                {total} total · {leads.opportunities.length} opportunity · {leads.contacts.length}{" "}
                contact
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="leads-refresh-button"
                onClick={() => load(sessionStorage.getItem(STORAGE_KEY) || code)}
                disabled={loading}
                className={btnGhost}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <button data-testid="leads-signout-button" onClick={signOut} className={btnGhost}>
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          </div>

          {leads.site && leads.site.total > 0 && (
            <div data-testid="leads-site-stats" className="mt-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-subtext">
                Website traffic
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="border border-ink bg-ink px-3 py-1.5 font-mono text-[11px] tracking-[0.1em] text-paper">
                  Total · {leads.site.total}
                </span>
                {Object.entries(leads.site.pages)
                  .sort((a, b) => b[1] - a[1])
                  .map(([p, n]) => (
                    <span
                      key={p}
                      data-testid={`leads-site-chip-${p.replace(/[^a-z0-9]/gi, "") || "home"}`}
                      className="border border-line bg-white px-3 py-1.5 font-mono text-[11px] tracking-[0.1em] text-ink"
                    >
                      {p === "/" ? "Home" : p.replace(/^\//, "")} · {n}
                    </span>
                  ))}
              </div>
            </div>
          )}

          {allCodes.length > 0 && (
            <div data-testid="leads-code-summary" className="mt-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-subtext">
                Ad performance
              </p>
              <div className="mt-3 max-w-xl overflow-x-auto border border-line bg-white">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.18em] text-subtext">
                      <th className="px-4 py-3 font-medium">Code</th>
                      <th className="px-4 py-3 font-medium">Views</th>
                      <th className="px-4 py-3 font-medium">Applications</th>
                      <th className="px-4 py-3 font-medium">Conv.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allCodes.map((c) => {
                      const views = (leads.stats || {})[c] || 0;
                      const apps = codeCounts[c] || 0;
                      const conv = views > 0 ? `${((apps / views) * 100).toFixed(1)}%` : "—";
                      return (
                        <tr
                          key={c}
                          data-testid={`leads-stats-row-${c}`}
                          className="border-b border-line last:border-b-0"
                        >
                          <td className="px-4 py-3 font-mono text-xs font-bold text-navy">{c}</td>
                          <td className="px-4 py-3 font-mono text-xs text-ink">{views || "—"}</td>
                          <td className="px-4 py-3 font-mono text-xs text-ink">{apps}</td>
                          <td className="px-4 py-3 font-mono text-xs text-ink">{conv}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 max-w-xl font-mono text-[10px] uppercase tracking-[0.15em] text-subtext">
                Views count once per visitor session · codes typed by hand show applications only
              </p>
            </div>
          )}

          <div className="mt-12">
            <h2 className="font-mono text-[12px] uppercase tracking-[0.18em] text-navy">
              Opportunity Applications ({leads.opportunities.length})
            </h2>
            {leads.opportunities.length === 0 ? (
              <p
                data-testid="leads-opportunities-empty"
                className="mt-6 flex items-center gap-2 border border-dashed border-line p-6 text-sm text-subtext"
              >
                <Inbox className="h-4 w-4" /> No applications yet.
              </p>
            ) : (
              <div className="mt-6 space-y-4">
                {leads.opportunities.map((l, i) => (
                  <LeadShell
                    key={l.id || i}
                    testid={`opportunity-lead-card-${i}`}
                    name={l.name}
                    email={l.email}
                    phone={l.phone}
                    linkedin={l.linkedin}
                    date={l.created_at}
                    flagged={l.flagged_spam}
                    appCode={l.application_code}
                    onDelete={() => remove("opportunities", l.id)}
                  >
                    <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-3">
                      <Detail label="Three strongest reasons" value={l.reasons} />
                      <Detail label="Sales background" value={l.background} />
                      <Detail label="Why build a business" value={l.why} />
                    </div>
                  </LeadShell>
                ))}
              </div>
            )}
          </div>

          <div className="mt-14">
            <h2 className="font-mono text-[12px] uppercase tracking-[0.18em] text-navy">
              Contact Inquiries ({leads.contacts.length})
            </h2>
            {leads.contacts.length === 0 ? (
              <p
                data-testid="leads-contacts-empty"
                className="mt-6 flex items-center gap-2 border border-dashed border-line p-6 text-sm text-subtext"
              >
                <Inbox className="h-4 w-4" /> No inquiries yet.
              </p>
            ) : (
              <div className="mt-6 space-y-4">
                {leads.contacts.map((l, i) => (
                  <LeadShell
                    key={l.id || i}
                    testid={`contact-lead-card-${i}`}
                    name={l.name}
                    email={l.email}
                    date={l.created_at}
                    onDelete={() => remove("contacts", l.id)}
                  >
                    <div className="mt-5 grid grid-cols-2 gap-5 md:grid-cols-4">
                      <Detail label="Company" value={l.company} />
                      <Detail label="Phone" value={l.phone} />
                      <Detail label="Agency size" value={l.agency_size} />
                      <Detail label="Inquiry type" value={l.inquiry_type} />
                    </div>
                    <div className="mt-5">
                      <Detail label="Message" value={l.message} />
                    </div>
                  </LeadShell>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
